import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, X, Save, ImagePlus } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("bct_token")}` });
const emptyProject = { title: "", location: "", date: "", image: "", images: [], description: "", long_description: "", category: "konut", status: "ongoing", progress: 0, area: "", client_name: "", start_date: "", end_date: "", features: [] };

const categoryLabels = { konut: "Konut", ticari: "Ticari", endustriyel: "Endüstriyel", altyapi: "Alt Yapı" };

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProject);
  const [loading, setLoading] = useState(false);
  const [featureInput, setFeatureInput] = useState("");

  const load = () => axios.get(`${API}/api/projects`, { headers: getHeaders() }).then((r) => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyProject); setEditing("new"); };
  const openEdit = (p) => {
    setForm({
      title: p.title, location: p.location, date: p.date, image: p.image,
      images: p.images || [], description: p.description, long_description: p.long_description || "",
      category: p.category || "konut", status: p.status, progress: p.progress,
      area: p.area || "", client_name: p.client_name || "",
      start_date: p.start_date || "", end_date: p.end_date || "",
      features: p.features || []
    });
    setEditing(p);
  };
  const close = () => { setEditing(null); setForm(emptyProject); setFeatureInput(""); };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (editing === "new") await axios.post(`${API}/api/admin/projects`, form, { headers: getHeaders(), withCredentials: true });
      else await axios.put(`${API}/api/admin/projects/${editing.id}`, form, { headers: getHeaders(), withCredentials: true });
      close(); load();
    } catch { alert("Hata oluştu"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu projeyi silmek istediğinize emin misiniz?")) return;
    await axios.delete(`${API}/api/admin/projects/${id}`, { headers: getHeaders(), withCredentials: true });
    load();
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setForm({ ...form, features: [...form.features, featureInput.trim()] });
      setFeatureInput("");
    }
  };

  const removeFeature = (i) => {
    setForm({ ...form, features: form.features.filter((_, idx) => idx !== i) });
  };

  const addGalleryImage = (url) => {
    if (url) setForm({ ...form, images: [...form.images, url] });
  };

  const removeGalleryImage = (i) => {
    setForm({ ...form, images: form.images.filter((_, idx) => idx !== i) });
  };

  return (
    <div data-testid="admin-projects">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit" }}>Projeler</h1>
        <button onClick={openNew} className="flex items-center gap-2 bg-[#FF5A00] hover:bg-[#e64f00] text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors" data-testid="add-project-btn">
          <Plus size={16} /> Yeni Proje
        </button>
      </div>

      <div className="bg-[#161923] border border-white/5 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="projects-table">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Proje</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Konum</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Kategori</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Durum</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">İlerleme</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`project-row-${p.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <img src={p.image?.startsWith("/api") ? `${API}${p.image}` : p.image} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                      <div>
                        <p className="text-white font-medium">{p.title}</p>
                        <p className="text-slate-500 text-xs">{p.date}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-slate-400 hidden md:table-cell">{p.location}</td>
                  <td className="py-3 px-4 hidden sm:table-cell">
                    <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-[#FF5A00]/10 text-[#FF5A00]">
                      {categoryLabels[p.category] || p.category}
                    </span>
                  </td>
                  <td className="py-3 px-4 hidden sm:table-cell">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${p.status === "ongoing" ? "bg-blue-500/10 text-blue-400" : "bg-emerald-500/10 text-emerald-400"}`}>
                      {p.status === "ongoing" ? "Devam Ediyor" : "Tamamlandı"}
                    </span>
                  </td>
                  <td className="py-3 px-4 hidden lg:table-cell">
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-[#FF5A00] rounded-full" style={{ width: `${p.progress}%` }} />
                      </div>
                      <span className="text-slate-500 text-xs">%{p.progress}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(p)} className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"><Pencil size={14} /></button>
                      <button onClick={() => handleDelete(p.id)} className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {projects.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-500">Henüz proje eklenmedi</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="project-modal">
          <div className="bg-[#1a1d27] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: "Outfit" }}>{editing === "new" ? "Yeni Proje" : "Proje Düzenle"}</h2>
              <button onClick={close} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Başlık</label>
                  <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="admin-input" data-testid="form-title" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Konum</label>
                  <input type="text" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="admin-input" data-testid="form-location" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Tarih</label>
                  <input type="text" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="admin-input" placeholder="2024 - Devam" data-testid="form-date" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Kategori</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="admin-input" data-testid="form-category">
                    <option value="konut">Konut</option>
                    <option value="ticari">Ticari</option>
                    <option value="endustriyel">Endüstriyel</option>
                    <option value="altyapi">Alt Yapı</option>
                  </select>
                </div>
              </div>

              <ImageUploader value={form.image} onChange={(v) => setForm({ ...form, image: v })} label="Ana Görsel" />

              {/* Gallery Images */}
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Ek Görseller</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {form.images.map((img, i) => (
                    <div key={i} className="relative w-20 h-14 rounded-lg overflow-hidden group">
                      <img src={img.startsWith("/api") ? `${API}${img}` : img} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => removeGalleryImage(i)} className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        <X size={14} className="text-white" />
                      </button>
                    </div>
                  ))}
                </div>
                <ImageUploader value="" onChange={addGalleryImage} label="" />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Kısa Açıklama</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="admin-input resize-none" data-testid="form-description" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Detaylı Açıklama</label>
                <textarea value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} rows={3} className="admin-input resize-none" data-testid="form-long-description" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Durum</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="admin-input" data-testid="form-status">
                    <option value="ongoing">Devam Ediyor</option>
                    <option value="completed">Tamamlandı</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">İlerleme %</label>
                  <input type="number" min={0} max={100} value={form.progress} onChange={(e) => setForm({ ...form, progress: parseInt(e.target.value) || 0 })} className="admin-input" data-testid="form-progress" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Proje Alanı</label>
                  <input type="text" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className="admin-input" placeholder="45.000 m²" data-testid="form-area" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Müşteri</label>
                  <input type="text" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="admin-input" data-testid="form-client" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Başlangıç Tarihi</label>
                  <input type="text" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="admin-input" placeholder="2024-01" data-testid="form-start-date" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Bitiş Tarihi</label>
                  <input type="text" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="admin-input" placeholder="2025-12" data-testid="form-end-date" />
                </div>
              </div>

              {/* Features */}
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Proje Özellikleri</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {form.features.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FF5A00]/10 text-[#FF5A00] text-xs font-medium">
                      {f}
                      <button onClick={() => removeFeature(i)} className="hover:text-white transition-colors"><X size={12} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input type="text" value={featureInput} onChange={(e) => setFeatureInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())} className="admin-input flex-1" placeholder="Özellik ekle..." data-testid="form-feature-input" />
                  <button onClick={addFeature} className="px-3 py-2 bg-[#FF5A00]/20 text-[#FF5A00] rounded-lg text-sm hover:bg-[#FF5A00]/30 transition-colors">Ekle</button>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-white/5">
              <button onClick={close} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white border border-white/10 transition-colors">İptal</button>
              <button onClick={handleSave} disabled={loading || !form.title} className="flex items-center gap-2 px-4 py-2 bg-[#FF5A00] hover:bg-[#e64f00] text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50" data-testid="form-save">
                <Save size={14} /> {loading ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
